import React, { useMemo, useState, useEffect } from "react";
import axiosInstance from "../api/axiosInstance";
import { humanizeError } from "../utils/userFriendlyError";
import { useToast } from "../context/ToastContext";
import { formatAccountName } from "../utils/formatters";
import { NIGO_REASON_OPTIONS } from "../constants/householdConstants";

const NOTES_MAX = 100;

const buildInitialPerAccount = (accounts) => {
  const initial = {};
  (accounts || []).forEach((a) => {
    if (a?.status === "nigo") {
      initial[a.id] = {
        reason: a.sub_status || "",
        notes: a.notes || "",
      };
    }
  });
  return initial;
};

const normalizeEntries = (perAccount) =>
  Object.entries(perAccount)
    .map(([id, v]) => [
      id,
      { reason: v?.reason || "", notes: (v?.notes || "").trim() },
    ])
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));

function MarkNigoModal({
  isOpen,
  onClose,
  householdId,
  accounts,
  members,
  onSuccess,
}) {
  const { toast } = useToast();
  const initialPerAccount = useMemo(
    () => buildInitialPerAccount(accounts),
    [accounts],
  );
  const [perAccount, setPerAccount] = useState(initialPerAccount);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  // Re-hydrate whenever the modal opens or the underlying accounts change.
  useEffect(() => {
    if (isOpen) {
      setPerAccount(buildInitialPerAccount(accounts));
      setErrorMessage(null);
    }
  }, [isOpen, accounts]);

  const selectableAccounts = useMemo(() => {
    return (accounts || []).filter((a) => a?.status !== "inactive");
  }, [accounts]);

  const selectedEntries = Object.entries(perAccount);
  const allHaveReason =
    selectedEntries.length > 0 &&
    selectedEntries.every(([, v]) => !!v.reason);
  const allNotesWithinLimit = selectedEntries.every(
    ([, v]) => (v.notes || "").length <= NOTES_MAX,
  );
  const hasDiff = useMemo(() => {
    const a = JSON.stringify(normalizeEntries(perAccount));
    const b = JSON.stringify(normalizeEntries(initialPerAccount));
    return a !== b;
  }, [perAccount, initialPerAccount]);

  if (!isOpen) return null;

  const toggleAccount = (id) => {
    setPerAccount((prev) => {
      const next = { ...prev };
      if (next[id]) {
        delete next[id];
      } else {
        next[id] = { reason: "", notes: "" };
      }
      return next;
    });
  };

  const updateField = (id, field, value) => {
    setPerAccount((prev) => ({
      ...prev,
      [id]: { ...(prev[id] || { reason: "", notes: "" }), [field]: value },
    }));
  };

  const canSubmit =
    !submitting &&
    selectedEntries.length > 0 &&
    allHaveReason &&
    allNotesWithinLimit &&
    hasDiff;

  const handleClose = () => {
    if (submitting) return;
    setPerAccount(initialPerAccount);
    setErrorMessage(null);
    onClose();
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setErrorMessage(null);
    try {
      const nigo_accounts = selectedEntries.map(([account_id, v]) => {
        const item = { account_id, nigo_reason: v.reason };
        const trimmed = (v.notes || "").trim();
        if (trimmed) item.notes = trimmed;
        return item;
      });
      const response = await axiosInstance.put(
        `/households/${householdId}/status`,
        { status: "nigo", nigo_accounts },
      );
      toast.success("Household NIGO accounts updated.");
      onSuccess?.(response.data);
      onClose();
    } catch (err) {
      setErrorMessage(humanizeError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-[2px]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="mark-nigo-title"
      onClick={handleClose}
    >
      <div
        className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-red-100 bg-white shadow-2xl shadow-red-900/10"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-red-100 bg-gradient-to-r from-red-50/90 to-rose-50/50 px-5 py-4">
          <div>
            <h2 id="mark-nigo-title" className="text-lg font-bold text-red-950">
              Mark Household as NIGO
            </h2>
            <p className="mt-0.5 text-xs font-medium text-red-800/70">
              Select the NIGO accounts provide an optional reason.
            </p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="rounded-xl p-2 text-red-800/60 transition-colors hover:bg-white/80 hover:text-red-900"
            aria-label="Close"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5 space-y-4">
            <section>
              <label className="block text-sm font-semibold text-gray-800 mb-2">
                NIGO Accounts
              </label>
              {selectableAccounts.length === 0 ? (
                <p className="text-sm text-gray-500 italic">
                  No accounts available to mark as NIGO.
                </p>
              ) : (
                <div className="flex flex-col gap-3">
                  {selectableAccounts.map((account, idx) => {
                    const entry = perAccount[account.id];
                    const checked = !!entry;
                    const alreadyNigo = account.status === "nigo";
                    const notesLen = (entry?.notes || "").length;
                    const notesOver = notesLen > NOTES_MAX;
                    return (
                      <div
                        key={account.id}
                        className={`rounded-lg border transition-colors ${
                          checked
                            ? "border-red-300 bg-red-50"
                            : "border-gray-200 hover:border-red-200"
                        }`}
                      >
                        <label className="flex cursor-pointer items-center gap-3 px-3 py-2">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleAccount(account.id)}
                            className="h-4 w-4 rounded text-red-600 focus:ring-red-500"
                          />
                          <span className="flex-1 text-sm font-medium text-gray-900">
                            {formatAccountName(account, idx + 1, members || [])}
                          </span>
                          {alreadyNigo && (
                            <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-red-800">
                              NIGO
                            </span>
                          )}
                        </label>

                        {checked && (
                          <div className="grid grid-cols-1 gap-3 border-t border-red-200/60 px-3 py-3 md:grid-cols-2">
                            <div>
                              <label className="block text-xs font-semibold text-gray-700 mb-1">
                                NIGO Reason <span className="text-red-600">*</span>
                              </label>
                              <select
                                value={entry.reason}
                                onChange={(e) =>
                                  updateField(account.id, "reason", e.target.value)
                                }
                                className="w-full rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                              >
                                <option value="">Select a reason</option>
                                {NIGO_REASON_OPTIONS.map((opt) => (
                                  <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <div className="flex items-center justify-between mb-1">
                                <label className="block text-xs font-semibold text-gray-700">
                                  Notes
                                </label>
                                <span
                                  className={`text-[10px] font-medium ${
                                    notesOver ? "text-red-600" : "text-gray-500"
                                  }`}
                                >
                                  {notesLen}/{NOTES_MAX}
                                </span>
                              </div>
                              <input
                                type="text"
                                value={entry.notes}
                                maxLength={NOTES_MAX}
                                onChange={(e) =>
                                  updateField(account.id, "notes", e.target.value)
                                }
                                placeholder="Optional"
                                className={`w-full rounded-md border bg-white px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 ${
                                  notesOver
                                    ? "border-red-400 focus:ring-red-500"
                                    : "border-gray-300 focus:ring-red-500"
                                }`}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </section>

            {errorMessage && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                {errorMessage}
              </div>
            )}
          </div>

          <div className="flex items-center justify-end gap-3 border-t border-gray-100 bg-gray-50/60 px-5 py-3">
            <button
              type="button"
              onClick={handleClose}
              disabled={submitting}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {submitting && (
                <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              )}
              Mark as NIGO
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default MarkNigoModal;
